export type JobStatus =
  | 'pending'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'waiting_user'

export type ExportStatus = 'pending' | 'processing' | 'completed' | 'failed'
export type ExportFormat = 'csv' | 'xlsx' | 'json'
export type HealthStatus = 'healthy' | 'degraded'

export interface JobConfig {
  headless: boolean
  min_delay_ms: number
  max_delay_ms: number
  max_retries: number
  save_raw_html: boolean
  skip_duplicates: boolean
  stop_on_captcha: boolean
  max_companies: number | null
  concurrency: number
  fast_export: boolean
}
export interface CompanyFilterParams {
  search?: string
  entity_type?: 'organizations' | 'entrepreneurs'
  active_only?: boolean
  okved?: string
  region?: string
  date_from?: string
  date_to?: string
  capital_min?: number
  revenue_min?: number
  revenue_max?: number
  profit_min?: number
  profit_max?: number
  assets_min?: number
  assets_max?: number
  taxes_min?: number
  taxes_max?: number
  has_phone?: boolean
  has_email?: boolean
  has_website?: boolean
  has_licenses?: boolean
  has_trademarks?: boolean
  has_procurements?: boolean
  director?: string
}

export interface StatusOption {
  value: string
  count: number
}

export interface OkvedOption {
  value: string
  count: number
}

export interface CompanyFilterOptions {
  statuses: StatusOption[]
  main_okveds: OkvedOption[]
  total_active: number
  total_with_phone: number
  total_with_email: number
  total_with_website: number
  total_with_licenses: number
  total_with_trademarks: number
  total_with_procurements: number
}

export interface JobProgress {
  total_pages: number
  processed_pages: number
  total_companies: number
  scraped_companies: number
  failed_companies: number
  progress_percent: number
}

export interface Job {
  id: number
  name: string | null
  source_url: string
  status: JobStatus
  config: JobConfig
  progress: JobProgress
  error_message: string | null
  created_at: string
  updated_at: string
  started_at: string | null
  completed_at: string | null
}

export interface JobCreateRequest {
  source_url: string
  name?: string
  config?: Partial<JobConfig>
}

export interface JobListResponse {
  items: Job[]
  total: number
  page: number
  page_size: number
  pages: number
}

export interface JobActionResponse {
  success: boolean
  message: string
  details?: Record<string, unknown> | null
  job: Job
}

export interface Company {
  id: number
  job_id: number
  company_name: string | null
  company_url: string
  ogrn: string | null
  inn: string | null
  kpp: string | null
  okpo: string | null
  registration_date: string | null
  status: string | null
  director: string | null
  director_inn: string | null
  legal_address: string | null
  actual_address: string | null
  phone: string | null
  email: string | null
  website: string | null
  authorized_capital: number | null
  main_okved: string | null
  additional_okveds: string[] | null
  revenue: number | null
  net_profit: number | null
  assets: number | null
  taxes_and_fees: number | null
  procurements_summary: Record<string, unknown> | null
  inspections_summary: Record<string, unknown> | null
  arbitration_summary: Record<string, unknown> | null
  licenses_summary: Record<string, unknown> | null
  trademarks_summary: Record<string, unknown> | null
  scraped_at: string
  parsing_errors: string[] | null
  is_duplicate: boolean
}

export interface CompanyListResponse {
  items: Company[]
  total: number
  page: number
  page_size: number
  pages: number
}

export interface LogEntry {
  id: number
  level: string
  message: string
  details: Record<string, unknown> | null
  created_at: string
}

export interface LogsResponse {
  items: LogEntry[]
  total: number
  page: number
  page_size: number
}

export interface Export {
  id: number
  job_id: number
  filename: string
  filepath: string
  format: ExportFormat
  status: ExportStatus
  row_count: number
  file_size_bytes: number
  error_message: string | null
  created_at: string
  completed_at: string | null
}

export interface ExportListResponse {
  items: Export[]
  total: number
}

export interface SettingsBundle {
  settings: Record<string, Record<string, unknown>>
}

export interface HealthCheck {
  status: HealthStatus
  version: string
  database: string
  redis: string
  timestamp: string
}

/* ─── Checko.ru Advanced Search Filter Params ─── */

export type EntityType = 'organizations' | 'entrepreneurs'
export type SmbCategory = 0 | 1 | 2 | 3
export type TaxRegime = 0 | 1 | 2 | 3 | 4

export interface CheckoFilterParams {
  search?: string
  entity?: EntityType
  active?: boolean
  activities?: string[]
  locations?: string[]
  date_from?: string
  date_to?: string
  smb?: boolean
  smb_code?: SmbCategory
  employee?: boolean
  employee_min?: number
  employee_max?: number
  capital?: boolean
  capital_min?: number
  fs_2110?: boolean
  fs_2110_min?: number
  fs_2110_max?: number
  fs_2400?: boolean
  fs_2400_min?: number
  fs_2400_max?: number
  fs_1300?: boolean
  fs_1300_min?: number
  fs_1300_max?: number
  tax_treatment?: boolean
  tax_treatment_code?: TaxRegime
  tax_amount?: boolean
  tax_amount_min?: number
  cn?: boolean
  cncs_min?: number
  cnsp_min?: number
  phones?: boolean
  emails?: boolean
  websites?: boolean
  licenses?: boolean
  trademarks?: boolean
  leasing?: boolean
}

export function buildCheckoUrl(params: CheckoFilterParams): string {
  const qs = new URLSearchParams()
  const base = 'https://checko.ru/search/advanced'

  if (params.entity) qs.set('entity', params.entity)
  if (params.active !== undefined) qs.set('active', String(params.active))
  if (params.search) qs.set('search', params.search)
  if (params.date_from) qs.set('date_from', params.date_from)
  if (params.date_to) qs.set('date_to', params.date_to)

  // Array params: activities[] and locations[]
  if (params.activities?.length) {
    params.activities.forEach(a => qs.append('activities[]', a))
  }
  if (params.locations?.length) {
    params.locations.forEach(l => qs.append('locations[]', l))
  }

  // SMB
  if (params.smb) {
    qs.set('smb', '1')
    if (params.smb_code !== undefined) qs.set('smb_code', String(params.smb_code))
  }

  // Employee headcount
  if (params.employee) {
    qs.set('employee', '1')
    if (params.employee_min !== undefined) qs.set('employee_min', String(params.employee_min))
    if (params.employee_max !== undefined) qs.set('employee_max', String(params.employee_max))
  }

  // Charter capital
  if (params.capital) {
    qs.set('capital', '1')
    if (params.capital_min !== undefined) qs.set('capital_min', String(params.capital_min))
  }

  // Financial reporting
  if (params.fs_2110) {
    qs.set('fs_2110', '1')
    if (params.fs_2110_min !== undefined) qs.set('fs_2110_min', String(params.fs_2110_min))
    if (params.fs_2110_max !== undefined) qs.set('fs_2110_max', String(params.fs_2110_max))
  }
  if (params.fs_2400) {
    qs.set('fs_2400', '1')
    if (params.fs_2400_min !== undefined) qs.set('fs_2400_min', String(params.fs_2400_min))
    if (params.fs_2400_max !== undefined) qs.set('fs_2400_max', String(params.fs_2400_max))
  }
  if (params.fs_1300) {
    qs.set('fs_1300', '1')
    if (params.fs_1300_min !== undefined) qs.set('fs_1300_min', String(params.fs_1300_min))
    if (params.fs_1300_max !== undefined) qs.set('fs_1300_max', String(params.fs_1300_max))
  }

  // Tax treatment
  if (params.tax_treatment) {
    qs.set('tax_treatment', '1')
    if (params.tax_treatment_code !== undefined) qs.set('tax_treatment_code', String(params.tax_treatment_code))
  }
  if (params.tax_amount) {
    qs.set('tax_amount', '1')
    if (params.tax_amount_min !== undefined) qs.set('tax_amount_min', String(params.tax_amount_min))
  }

  // Government procurement
  if (params.cn) {
    qs.set('cn', '1')
    if (params.cncs_min !== undefined) qs.set('cncs_min', String(params.cncs_min))
    if (params.cnsp_min !== undefined) qs.set('cnsp_min', String(params.cnsp_min))
  }

  // Contact toggles
  if (params.phones) qs.set('phones', '1')
  if (params.emails) qs.set('emails', '1')
  if (params.websites) qs.set('websites', '1')

  // Additional
  if (params.licenses) qs.set('licenses', '1')
  if (params.trademarks) qs.set('trademarks', '1')
  if (params.leasing) qs.set('leasing', '1')

  const str = qs.toString()
  return str ? `${base}?${str}` : base
}
